import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { Node, mergeAttributes } from '@tiptap/core';
import styled from 'styled-components';
import * as beautify from 'js-beautify';

// Custom extension to preserve style and script tags
const StyleScriptExtension = Node.create({
  name: 'styleScript',

  group: 'inline',

  inline: true,

  atom: true,

  addAttributes() {
    return {
      type: {
        default: 'style',
      },
      content: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'style',
        getAttrs: (node: any) => ({
          type: 'style',
          content: node.innerHTML,
        }),
      },
      {
        tag: 'script',
        getAttrs: (node: any) => ({
          type: 'script',
          content: node.innerHTML,
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const tag = HTMLAttributes.type || 'style';
    return [tag, mergeAttributes(HTMLAttributes), HTMLAttributes.content || ''];
  },
});

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  overflow: hidden;
`;

const ModeToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModeToggleLabel = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  font-weight: 500;
`;

const ModeToggle = styled.button<{ $active?: boolean }>`
  padding: 0.4rem 0.75rem;
  background: ${props => props.$active ? 'rgba(139, 92, 246, 0.3)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$active ? 'rgba(139, 92, 246, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 6px;
  color: ${props => props.$active ? '#8b5cf6' : 'rgba(255, 255, 255, 0.7)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  font-weight: 500;

  &:hover {
    background: ${props => props.$active ? 'rgba(139, 92, 246, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const CodeEditorContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  margin: 0.5rem;

  textarea {
    width: 100%;
    min-height: 500px;
    height: 100%;
    padding: 1.5rem;
    background: transparent;
    border: none;
    color: #a5b4fc;
    font-family: 'Fira Code', 'Monaco', 'Consolas', 'Courier New', monospace;
    font-size: 0.875rem;
    line-height: 1.6;
    resize: vertical;
    outline: none;
    display: block;
    box-sizing: border-box;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.3);
    }

    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.2);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(139, 92, 246, 0.3);
      border-radius: 4px;

      &:hover {
        background: rgba(139, 92, 246, 0.5);
      }
    }
  }
`;

const EditorToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  align-items: center;
`;

const ToolbarButton = styled.button<{ $active?: boolean }>`
  padding: 0.5rem 0.75rem;
  background: ${props => props.$active ? 'rgba(139, 92, 246, 0.3)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$active ? 'rgba(139, 92, 246, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 6px;
  color: ${props => props.$active ? '#8b5cf6' : 'rgba(255, 255, 255, 0.7)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;

  &:hover:not(:disabled) {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.4);
    color: #8b5cf6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  & svg {
    width: 18px;
    height: 18px;
  }
`;

const ToolbarSeparator = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0 0.5rem;
`;

const ColorPicker = styled.input`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  background: transparent;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
  }
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: rgba(30, 30, 40, 0.95);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  min-width: 400px;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`;

const ModalTitle = styled.h3`
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
`;

const ModalBody = styled.div`
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1rem;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Input = styled.input`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  width: 100%;
  margin-bottom: 0.75rem;

  &:focus {
    outline: none;
    border-color: rgba(139, 92, 246, 0.5);
    background: rgba(0, 0, 0, 0.4);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const Label = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$variant === 'primary' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$variant === 'primary' ? 'rgba(139, 92, 246, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  color: rgba(255, 255, 255, 0.9);

  &:hover {
    background: ${props => props.$variant === 'primary' ? 'rgba(139, 92, 246, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 4px;
  margin: 1rem 0;
`;

const GridCell = styled.div<{ $selected?: boolean }>`
  aspect-ratio: 1;
  background: ${props => props.$selected ? 'rgba(139, 92, 246, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(139, 92, 246, 0.3);
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Tab = styled.button<{ $active?: boolean }>`
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  color: ${props => props.$active ? 'rgba(139, 92, 246, 1)' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.$active ? 'rgba(139, 92, 246, 1)' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    color: rgba(139, 92, 246, 0.8);
  }
`;

const SymbolGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 0.5rem;
  margin: 1rem 0;
`;

const SymbolButton = styled.button`
  aspect-ratio: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(139, 92, 246, 0.3);
    border-color: rgba(139, 92, 246, 0.5);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  margin: 1rem 0;
`;

// Table Edit Dropdown
const TableEditDropdown = styled.div<{ $visible: boolean; $showAbove?: boolean }>`
  position: absolute;
  ${props => props.$showAbove ? 'bottom: 100%;' : 'top: 100%;'}
  left: 0;
  margin-top: ${props => props.$showAbove ? '0' : '0.25rem'};
  margin-bottom: ${props => props.$showAbove ? '0.25rem' : '0'};
  background: rgba(30, 30, 40, 0.98);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 8px;
  padding: 0.5rem;
  min-width: 220px;
  max-width: 300px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  display: ${props => props.$visible ? 'block' : 'none'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.5);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(139, 92, 246, 0.7);
  }
`;

const DropdownSection = styled.div`
  padding: 0.25rem 0;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const DropdownLabel = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(139, 92, 246, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: transparent;
    }
  }

  & svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

// Table Insertion Modal
interface TableModalProps {
  onClose: () => void;
  onInsert: (rows: number, cols: number) => void;
}

const TableModal: React.FC<TableModalProps> = ({ onClose, onInsert }) => {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [inputRows, setInputRows] = useState('3');
  const [inputCols, setInputCols] = useState('3');

  const handleCellClick = (rowIndex: number, colIndex: number) => {
    const newRows = rowIndex + 1;
    const newCols = colIndex + 1;
    setRows(newRows);
    setCols(newCols);
    setInputRows(newRows.toString());
    setInputCols(newCols.toString());
  };

  const handleRowsInputChange = (value: string) => {
    setInputRows(value);
    const num = parseInt(value) || 1;
    const clamped = Math.min(Math.max(num, 1), 20);
    setRows(clamped);
  };

  const handleColsInputChange = (value: string) => {
    setInputCols(value);
    const num = parseInt(value) || 1;
    const clamped = Math.min(Math.max(num, 1), 20);
    setCols(clamped);
  };

  const handleInsert = () => {
    onInsert(rows, cols);
    onClose();
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>插入表格</ModalTitle>
        <ModalBody>
          <InputGroup>
            <Label>表格大小 (行 × 列)</Label>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <Label htmlFor="rowsInput">行数</Label>
                <Input
                  id="rowsInput"
                  type="number"
                  min="1"
                  max="20"
                  value={inputRows}
                  onChange={(e) => handleRowsInputChange(e.target.value)}
                />
              </div>
              <span style={{ fontSize: '1.5rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '1rem' }}>×</span>
              <div style={{ flex: 1 }}>
                <Label htmlFor="colsInput">列数</Label>
                <Input
                  id="colsInput"
                  type="number"
                  min="1"
                  max="20"
                  value={inputCols}
                  onChange={(e) => handleColsInputChange(e.target.value)}
                />
              </div>
            </div>
            <Label>或点击下方网格选择</Label>
            <GridContainer>
              {Array.from({ length: 10 }).map((_, rowIndex) =>
                Array.from({ length: 10 }).map((_, colIndex) => {
                  const isSelected = rowIndex < rows && colIndex < cols;
                  return (
                    <GridCell
                      key={`${rowIndex}-${colIndex}`}
                      $selected={isSelected}
                      onClick={() => handleCellClick(rowIndex, colIndex)}
                    />
                  );
                })
              )}
            </GridContainer>
          </InputGroup>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>取消</Button>
          <Button $variant="primary" onClick={handleInsert}>插入表格</Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

// Image Insertion Modal
interface ImageModalProps {
  onClose: () => void;
  onInsert: (url: string) => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ onClose, onInsert }) => {
  const [activeTab, setActiveTab] = useState<'url' | 'upload'>('url');
  const [url, setUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const token = localStorage.getItem('auth-storage')
    ? JSON.parse(localStorage.getItem('auth-storage')!).state.token
    : null;

  const handleUrlChange = (value: string) => {
    setUrl(value);
    setPreviewUrl(value);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Show preview using FileReader
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setPreviewUrl(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async () => {
    if (!selectedFile || !token) {
      if (!token) {
        alert('请先登录');
      }
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch(`${API_BASE_URL}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // Return full URL - remove /api from base URL for static files
        const baseUrl = API_BASE_URL.replace('/api', '');
        const fullUrl = `${baseUrl}${data.data.imageUrl}`;
        onInsert(fullUrl);
        onClose();
      } else {
        alert(`上传失败: ${data.message}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('上传失败，请重试');
    } finally {
      setIsUploading(false);
    }
  };

  const handleInsert = () => {
    if (activeTab === 'upload') {
      handleUploadImage();
    } else if (url) {
      onInsert(url);
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>插入图片</ModalTitle>
        <TabsContainer>
          <Tab $active={activeTab === 'url'} onClick={() => setActiveTab('url')}>URL链接</Tab>
          <Tab $active={activeTab === 'upload'} onClick={() => setActiveTab('upload')}>本地上传</Tab>
        </TabsContainer>
        <ModalBody>
          {activeTab === 'url' ? (
            <InputGroup>
              <Label>图片URL</Label>
              <Input
                type="text"
                placeholder="https://example.com/image.jpg"
                value={url}
                onChange={(e) => handleUrlChange(e.target.value)}
              />
              {previewUrl && <PreviewImage src={previewUrl} alt="预览" />}
            </InputGroup>
          ) : (
            <InputGroup>
              <Label>选择文件</Label>
              <Button onClick={() => fileInputRef.current?.click()}>
                选择图片文件
              </Button>
              <FileInput
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
              />
              {previewUrl && <PreviewImage src={previewUrl} alt="预览" />}
            </InputGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>取消</Button>
          <Button
            $variant="primary"
            onClick={handleInsert}
            disabled={(activeTab === 'url' && !url) || (activeTab === 'upload' && (!selectedFile || isUploading))}
          >
            {isUploading ? '上传中...' : '插入'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

// Math/Symbol Modal
interface MathModalProps {
  onClose: () => void;
  onInsert: (content: string) => void;
}

const MathModal: React.FC<MathModalProps> = ({ onClose, onInsert }) => {
  const [activeTab, setActiveTab] = useState<'equation' | 'symbols'>('equation');
  const [equation, setEquation] = useState('');

  const symbolCategories = {
    'Basic': ['+', '-', '×', '÷', '=', '≠', '≈', '±', '∓', '√'],
    'Comparison': ['<', '>', '≤', '≥', '∈', '∉', '⊂', '⊃', '∪', '∩'],
    'Greek': ['α', 'β', 'γ', 'δ', 'ε', 'θ', 'λ', 'μ', 'π', 'σ', 'φ', 'ω', 'Δ', 'Σ', 'Ω'],
    'Arrows': ['→', '←', '↑', '↓', '⇒', '⇐', '⇑', '⇓', '↔', '⇔'],
    'Math': ['∞', '∂', '∇', '∫', '∑', '∏', '√', '∛', '∜', '°'],
  };

  const equationTemplates = [
    { name: '分数', template: '\\frac{a}{b}' },
    { name: '平方根', template: '\\sqrt{x}' },
    { name: '求和', template: '\\sum_{i=1}^{n}' },
    { name: '积分', template: '\\int_{a}^{b}' },
    { name: '上标', template: 'x^{2}' },
    { name: '下标', template: 'x_{i}' },
  ];

  const handleSymbolInsert = (symbol: string) => {
    onInsert(symbol);
    onClose();
  };

  const handleEquationInsert = () => {
    if (equation) {
      // For KaTeX/LaTeX equations, wrap in code block
      onInsert(`$$${equation}$$`);
      onClose();
    }
  };

  const handleTemplateClick = (template: string) => {
    setEquation(template);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()} style={{ minWidth: '600px' }}>
        <ModalTitle>数学公式与符号</ModalTitle>
        <TabsContainer>
          <Tab $active={activeTab === 'equation'} onClick={() => setActiveTab('equation')}>公式</Tab>
          <Tab $active={activeTab === 'symbols'} onClick={() => setActiveTab('symbols')}>符号</Tab>
        </TabsContainer>
        <ModalBody>
          {activeTab === 'equation' ? (
            <>
              <InputGroup>
                <Label>LaTeX公式</Label>
                <Input
                  type="text"
                  placeholder="\frac{a}{b}"
                  value={equation}
                  onChange={(e) => {
                    setEquation(e.target.value);
                  }}
                />
              </InputGroup>
              <InputGroup>
                <Label>快速模板</Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {equationTemplates.map((tmpl) => (
                    <Button
                      key={tmpl.name}
                      onClick={() => handleTemplateClick(tmpl.template)}
                      style={{ fontSize: '0.75rem' }}
                    >
                      {tmpl.name}
                    </Button>
                  ))}
                </div>
              </InputGroup>
            </>
          ) : (
            <>
              <InputGroup>
                <Label>符号类别</Label>
                {Object.entries(symbolCategories).map(([category, symbols]) => (
                  <div key={category} style={{ marginBottom: '1rem' }}>
                    <Label>{category}</Label>
                    <SymbolGrid>
                      {symbols.map((symbol) => (
                        <SymbolButton
                          key={symbol}
                          onClick={() => handleSymbolInsert(symbol)}
                        >
                          {symbol}
                        </SymbolButton>
                      ))}
                    </SymbolGrid>
                  </div>
                ))}
              </InputGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>取消</Button>
          {activeTab === 'equation' && (
            <Button $variant="primary" onClick={handleEquationInsert} disabled={!equation}>
              插入
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

const EditorContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.5);
    border-radius: 4px;
  }

  .ProseMirror {
    outline: none;
    min-height: 300px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 1rem;
    line-height: 1.6;

    > * + * {
      margin-top: 0.75em;
    }

    h1, h2, h3, h4, h5, h6 {
      line-height: 1.2;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.95);
    }

    h1 { font-size: 2em; }
    h2 { font-size: 1.5em; }
    h3 { font-size: 1.25em; }

    code {
      background: rgba(0, 0, 0, 0.4);
      padding: 0.2em 0.4em;
      border-radius: 4px;
      font-size: 0.875em;
      color: #f472b6;
    }

    pre {
      background: rgba(0, 0, 0, 0.6);
      padding: 1rem;
      border-radius: 8px;
      overflow-x: auto;

      code {
        background: none;
        padding: 0;
        color: inherit;
      }
    }

    blockquote {
      border-left: 4px solid #8b5cf6;
      padding-left: 1rem;
      color: rgba(255, 255, 255, 0.7);
      font-style: italic;
    }

    ul, ol {
      padding-left: 1.5rem;
    }

    ul[data-type="taskList"] {
      list-style: none;
      padding: 0;

      li {
        display: flex;
        align-items: flex-start;

        > label {
          flex: 0 0 auto;
          margin-right: 0.5rem;
          user-select: none;
        }

        > div {
          flex: 1 1 auto;
        }
      }

      li input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
      }
    }

    a {
      color: #8b5cf6;
      text-decoration: underline;
      cursor: pointer;

      &:hover {
        color: #a78bfa;
      }
    }

    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 1rem 0;
    }

    table {
      border-collapse: collapse;
      table-layout: auto;
      width: 100%;
      margin: 1rem 0;
      overflow: visible;

      td, th {
        min-width: 50px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        padding: 0.5rem 0.75rem;
        vertical-align: top;
        box-sizing: border-box;
        position: relative;
        background-color: rgba(0, 0, 0, 0.2);

        > * {
          margin-bottom: 0;
        }

        /* Column resize handle */
        &::after {
          content: '';
          position: absolute;
          right: -3px;
          top: 0;
          bottom: 0;
          width: 6px;
          background: transparent;
          cursor: col-resize;
          transition: background 0.15s ease;
          z-index: 10;
        }

        &:hover::after {
          background: rgba(139, 92, 246, 0.6);
        }
      }

      /* Row resize handle */
      tr {
        position: relative;

        &::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: -3px;
          height: 6px;
          background: transparent;
          cursor: row-resize;
          transition: background 0.15s ease;
          z-index: 10;
        }

        &:hover::after {
          background: rgba(139, 92, 246, 0.4);
        }

        td, th {
          &:hover {
            background-color: rgba(139, 92, 246, 0.1);
          }
        }
      }

      th {
        font-weight: 600;
        text-align: left;
        background-color: rgba(139, 92, 246, 0.2);
        user-select: none;
      }

      .selectedCell:after {
        z-index: 2;
        position: absolute;
        content: "";
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background: rgba(139, 92, 246, 0.1);
        pointer: none;
      }

      p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        color: rgba(255, 255, 255, 0.3);
        pointer-events: none;
        height: 0;
      }
    }

    hr {
      border: none;
      border-top: 2px solid rgba(255, 255, 255, 0.1);
      margin: 2rem 0;
    }

    p.is-editor-empty:first-child::before {
      color: rgba(255, 255, 255, 0.3);
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }
  }
`;

// Custom hook for table resizing
const useTableResize = (editor: any) => {
  useEffect(() => {
    if (!editor) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if clicking on a column resize handle (right edge of cell)
      if (target.tagName === 'TD' || target.tagName === 'TH') {
        const cell = target as HTMLTableCellElement;
        const rect = target.getBoundingClientRect();
        const isRightEdge = e.clientX >= rect.right - 5 && e.clientX <= rect.right + 5;

        if (isRightEdge) {
          e.preventDefault();
          const table = target.closest('table');
          if (!table) return;

          const cellIndex = cell.cellIndex;
          const startX = e.clientX;
          const startWidth = target.offsetWidth;

          const handleMouseMove = (moveEvent: MouseEvent) => {
            const diff = moveEvent.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff);

            // Set width for all cells in this column
            const rows = table.querySelectorAll('tr');
            rows.forEach(row => {
              const tableCell = row.cells[cellIndex];
              if (tableCell) {
                tableCell.style.width = `${newWidth}px`;
                tableCell.style.minWidth = `${newWidth}px`;
                tableCell.style.maxWidth = `${newWidth}px`;
              }
            });
          };

          const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
          document.body.style.cursor = 'col-resize';
          document.body.style.userSelect = 'none';
        }
      }

      // Check if clicking on a row resize handle (bottom edge of row)
      if (target.tagName === 'TR' || target.closest('tr')) {
        const row = target.tagName === 'TR' ? target as HTMLTableRowElement : target.closest('tr') as HTMLTableRowElement;
        if (row) {
          const rect = row.getBoundingClientRect();
          const isBottomEdge = e.clientY >= rect.bottom - 5 && e.clientY <= rect.bottom + 5;

          if (isBottomEdge) {
            e.preventDefault();
            const table = row.closest('table');
            if (!table) return;

            const cells = row.querySelectorAll('td, th');
            const startHeights = Array.from(cells).map(cell => (cell as HTMLElement).offsetHeight);
            const startY = e.clientY;

            const handleMouseMove = (moveEvent: MouseEvent) => {
              const diff = moveEvent.clientY - startY;
              const newHeight = Math.max(30, startHeights[0] + diff);

              // Set height for all cells in this row
              cells.forEach((cell) => {
                const htmlCell = cell as HTMLElement;
                htmlCell.style.height = `${newHeight}px`;
                htmlCell.style.minHeight = `${newHeight}px`;
              });
            };

            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
              document.body.style.cursor = '';
              document.body.style.userSelect = '';
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'row-resize';
            document.body.style.userSelect = 'none';
          }
        }
      }
    };

    editor.view.dom.addEventListener('mousedown', handleMouseDown);

    return () => {
      if (editor.view && editor.view.dom) {
        editor.view.dom.removeEventListener('mousedown', handleMouseDown);
      }
    };
  }, [editor]);
};

const MenuBar: React.FC<{ editor: any }> = ({ editor }) => {
  const [showTableModal, setShowTableModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showMathModal, setShowMathModal] = useState(false);
  const [showTableEditMenu, setShowTableEditMenu] = useState(false);
  const [showDropdownAbove, setShowDropdownAbove] = useState(false);
  const tableButtonRef = useRef<HTMLDivElement>(null);

  const addLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('请输入链接URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }, [editor]);

  const handleInsertTable = useCallback((rows: number, cols: number) => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
  }, [editor]);

  const handleInsertImage = useCallback((url: string) => {
    if (!editor) return;
    editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const handleInsertMath = useCallback((content: string) => {
    if (!editor) return;
    // Insert math content
    editor.chain().focus().insertContent(content).run();
  }, [editor]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (tableButtonRef.current && !tableButtonRef.current.contains(target)) {
        const dropdown = document.querySelector('[data-table-dropdown]');
        if (dropdown && !dropdown.contains(target)) {
          setShowTableEditMenu(false);
        }
      }
    };

    // Calculate dropdown position
    const calculatePosition = () => {
      if (showTableEditMenu && tableButtonRef.current) {
        const rect = tableButtonRef.current.getBoundingClientRect();
        const dropdownHeight = 350; // Estimated max dropdown height
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          setShowDropdownAbove(true);
        } else {
          setShowDropdownAbove(false);
        }
      }
    };

    if (showTableEditMenu) {
      calculatePosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', calculatePosition, true);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [showTableEditMenu]);

  if (!editor) {
    return null;
  }

  return (
    <>
      <EditorToolbar>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          $active={editor.isActive('bold')}
          title="粗体"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
            <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          $active={editor.isActive('italic')}
          title="斜体"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="4" x2="10" y2="4" />
            <line x1="14" y1="20" x2="5" y2="20" />
            <line x1="15" y1="4" x2="9" y2="20" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          $active={editor.isActive('underline')}
          title="下划线"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
            <line x1="4" y1="21" x2="20" y2="21" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          $active={editor.isActive('strike')}
          title="删除线"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14" />
            <path d="M4 19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2" />
            <path d="M4 5a2 2 0 0 0 2-2h12a2 2 0 0 0 2 2" />
          </svg>
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          $active={editor.isActive('heading', { level: 1 })}
          title="标题1"
        >
          H1
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          $active={editor.isActive('heading', { level: 2 })}
          title="标题2"
        >
          H2
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          $active={editor.isActive('heading', { level: 3 })}
          title="标题3"
        >
          H3
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          $active={editor.isActive('bulletList')}
          title="无序列表"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <circle cx="3" cy="6" r="1" fill="currentColor" />
            <circle cx="3" cy="12" r="1" fill="currentColor" />
            <circle cx="3" cy="18" r="1" fill="currentColor" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          $active={editor.isActive('orderedList')}
          title="有序列表"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="10" y1="6" x2="21" y2="6" />
            <line x1="10" y1="12" x2="21" y2="12" />
            <line x1="10" y1="18" x2="21" y2="18" />
            <path d="M4 6h1v4" />
            <path d="M4 10h2" />
            <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          $active={editor.isActive('taskList')}
          title="任务列表"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 11 12 14 22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          $active={editor.isActive('blockquote')}
          title="引用"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
            <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          $active={editor.isActive('codeBlock')}
          title="代码块"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          $active={editor.isActive({ textAlign: 'left' })}
          title="左对齐"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="17" y1="10" x2="3" y2="10" />
            <line x1="21" y1="6" x2="3" y2="6" />
            <line x1="21" y1="14" x2="3" y2="14" />
            <line x1="17" y1="18" x2="3" y2="18" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          $active={editor.isActive({ textAlign: 'center' })}
          title="居中"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="21" y1="6" x2="3" y2="6" />
            <line x1="17" y1="10" x2="7" y2="10" />
            <line x1="19" y1="14" x2="5" y2="14" />
            <line x1="21" y1="18" x2="3" y2="18" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          $active={editor.isActive({ textAlign: 'right' })}
          title="右对齐"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="21" y1="6" x2="3" y2="6" />
            <line x1="21" y1="10" x2="9" y2="10" />
            <line x1="21" y1="14" x2="5" y2="14" />
            <line x1="21" y1="18" x2="7" y2="18" />
          </svg>
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          $active={editor.isActive('code')}
          title="行内代码"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
        </ToolbarButton>

        <ToolbarButton onClick={addLink} title="插入链接">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </ToolbarButton>

        <ToolbarButton onClick={() => setShowImageModal(true)} title="插入图片">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </ToolbarButton>

        {/* Table Button with Dropdown */}
        <div style={{ position: 'relative' }} ref={tableButtonRef as any}>
          <ToolbarButton
            onClick={() => setShowTableEditMenu(!showTableEditMenu)}
            title="表格"
            $active={showTableEditMenu}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="15" x2="21" y2="15" />
              <line x1="9" y1="3" x2="9" y2="21" />
              <line x1="15" y1="3" x2="15" y2="21" />
            </svg>
          </ToolbarButton>

          <TableEditDropdown $visible={showTableEditMenu} $showAbove={showDropdownAbove} data-table-dropdown="true">
            <DropdownSection>
              <DropdownLabel>插入表格</DropdownLabel>
              <DropdownButton onClick={() => { setShowTableModal(true); setShowTableEditMenu(false); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
                插入新表格
              </DropdownButton>
            </DropdownSection>

            {editor.isActive('table') && (
              <>
                <DropdownSection>
                  <DropdownLabel>行操作</DropdownLabel>
                  <DropdownButton
                    onClick={() => editor.chain().focus().addRowBefore().run()}
                    disabled={!editor.can().addRowBefore()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <polyline points="5 12 12 19 19 12" />
                    </svg>
                    在上方添加行
                  </DropdownButton>
                  <DropdownButton
                    onClick={() => editor.chain().focus().addRowAfter().run()}
                    disabled={!editor.can().addRowAfter()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <polyline points="19 12 12 5 5 12" />
                    </svg>
                    在下方添加行
                  </DropdownButton>
                  <DropdownButton
                    onClick={() => editor.chain().focus().deleteRow().run()}
                    disabled={!editor.can().deleteRow()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                    删除行
                  </DropdownButton>
                </DropdownSection>

                <DropdownSection>
                  <DropdownLabel>列操作</DropdownLabel>
                  <DropdownButton
                    onClick={() => editor.chain().focus().addColumnBefore().run()}
                    disabled={!editor.can().addColumnBefore()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <polyline points="5 12 12 19 19 12" />
                    </svg>
                    在左侧添加列
                  </DropdownButton>
                  <DropdownButton
                    onClick={() => editor.chain().focus().addColumnAfter().run()}
                    disabled={!editor.can().addColumnAfter()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <polyline points="19 12 12 5 5 12" />
                    </svg>
                    在右侧添加列
                  </DropdownButton>
                  <DropdownButton
                    onClick={() => editor.chain().focus().deleteColumn().run()}
                    disabled={!editor.can().deleteColumn()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                    删除列
                  </DropdownButton>
                </DropdownSection>

                <DropdownSection>
                  <DropdownLabel>表头操作</DropdownLabel>
                  <DropdownButton
                    onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                    切换表头行
                  </DropdownButton>
                  <DropdownButton
                    onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20v-6M6 20V10M18 20V4" />
                    </svg>
                    切换表头列
                  </DropdownButton>
                  <DropdownButton
                    onClick={() => editor.chain().focus().toggleHeaderCell().run()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
                    </svg>
                    切换表头单元格
                  </DropdownButton>
                </DropdownSection>

                <DropdownSection>
                  <DropdownLabel>表格操作</DropdownLabel>
                  <DropdownButton
                    onClick={() => editor.chain().focus().deleteTable().run()}
                    disabled={!editor.can().deleteTable()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                    删除表格
                  </DropdownButton>
                </DropdownSection>
              </>
            )}
          </TableEditDropdown>
        </div>

        <ToolbarButton onClick={() => setShowMathModal(true)} title="插入公式/符号">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 10h16" />
            <path d="M4 14h16" />
            <path d="M8 6l-4 12" />
            <path d="M16 6l4 12" />
            <path d="M12 8l-2 4" />
          </svg>
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={() => editor.chain().focus().setColor('#f59e0b').run()}
          title="橙色文字"
        >
          <ColorPicker type="color" value="#f59e0b" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setColor('#8b5cf6').run()}
          title="紫色文字"
        >
          <ColorPicker type="color" value="#8b5cf6" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().unsetColor().run()}
          title="清除颜色"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="撤销"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 7v6h6" />
            <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="重做"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 7v6h-6" />
            <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 3.7" />
          </svg>
        </ToolbarButton>

        {/* Modals */}
        {showTableModal && (
          <TableModal
            onClose={() => setShowTableModal(false)}
            onInsert={handleInsertTable}
          />
        )}

        {showImageModal && (
          <ImageModal
            onClose={() => setShowImageModal(false)}
            onInsert={handleInsertImage}
          />
        )}

        {showMathModal && (
          <MathModal
            onClose={() => setShowMathModal(false)}
            onInsert={handleInsertMath}
          />
        )}
      </EditorToolbar>
    </>
  );
};

interface TiptapEditorProps {
  content?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  editable?: boolean;
}

export const TiptapEditor: React.FC<TiptapEditorProps> = ({
  content = '',
  onChange,
  placeholder = '开始输入内容...',
  editable = true
}) => {
  const [editorMode, setEditorMode] = useState<'wysiwyg' | 'code'>('wysiwyg');
  const [codeContent, setCodeContent] = useState(content);
  const [rawHtmlContent, setRawHtmlContent] = useState(content); // Store raw HTML with style/script tags
  const codeEditorRef = useRef<HTMLTextAreaElement>(null);

  // Update code content when external content changes (only if not in code mode to avoid overwriting user input)
  useEffect(() => {
    if (editorMode !== 'code') {
      setCodeContent(content);
      setRawHtmlContent(content);
    }
  }, [content, editorMode]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty'
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank'
        }
      }),
      Image,
      Underline,
      TextAlign.configure({
        types: ['left', 'center', 'right']
      }),
      Table,
      TableRow,
      TableHeader,
      TableCell,
      TaskList,
      TaskItem,
      TextStyle,
      Color,
      StyleScriptExtension
    ] as any,
    content,
    editable: editable && editorMode === 'wysiwyg',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Only update code content if we're not in code mode (to avoid overwriting user's edits)
      if (editorMode !== 'code') {
        const formatted = formatHTML(html);
        setCodeContent(formatted);
      }
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-xl mx-auto focus:outline-none'
      }
    }
  });

  // Enable table resizing
  useTableResize(editor);

  // Update editor content when content prop changes from parent (e.g., when selecting different topics)
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentHtml = editor.getHTML();
      // Only update if the content is different to avoid unnecessary updates and cursor jumps
      if (content !== currentHtml) {
        editor.commands.setContent(content, {
          emitUpdate: false // Don't trigger the onUpdate callback
        });
      }
    }
  }, [content, editor]);

  // Keyboard shortcuts for mode switching
  useEffect(() => {
    if (!editable) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + Shift + H to toggle modes
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'H') {
        e.preventDefault();
        if (editorMode === 'wysiwyg') {
          handleSwitchToCode();
        } else {
          handleSwitchToWysiwyg();
        }
      }
      // Ctrl/Cmd + Shift + F to format code (only in code mode)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'F') {
        if (editorMode === 'code') {
          e.preventDefault();
          handleFormatCode();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editable, editorMode]);

  // Sync content ONLY when switching modes (not on every code change)
  useEffect(() => {
    if (editor) {
      if (editorMode === 'code' && editor) {
        // WYSIWYG -> Code: get HTML from editor when switching to code mode
        setCodeContent(editor.getHTML());
      }
    }
  }, [editorMode]);

  // Focus code editor when switching to code mode
  useEffect(() => {
    if (editorMode === 'code' && codeEditorRef.current) {
      codeEditorRef.current.focus();
    }
  }, [editorMode]);

  // Format HTML code for better readability
  const formatHTML = (html: string): string => {
    try {
      // First, beautify the HTML
      let formatted = beautify.html(html, {
        indent_size: 2,
        indent_char: ' ',
        max_preserve_newlines: 1,
        preserve_newlines: false,
        wrap_line_length: 0,
        wrap_attributes: 'auto',
        end_with_newline: false
      });

      // Then format inline style and script tags
      formatted = formatted.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, p1) => {
        try {
          const formattedCss = beautify.css(p1, {
            indent_size: 2,
            indent_char: ' '
          });
          return match.replace(p1, '\n' + formattedCss + '\n');
        } catch {
          return match;
        }
      });

      formatted = formatted.replace(/<script[^>]*>([\s\S]*?)<\/script>/gi, (match, p1) => {
        try {
          const formattedJs = beautify.js(p1, {
            indent_size: 2,
            indent_char: ' ',
            max_preserve_newlines: 1
          });
          return match.replace(p1, '\n' + formattedJs + '\n');
        } catch {
          return match;
        }
      });

      return formatted;
    } catch (error) {
      console.error('Error formatting HTML:', error);
      return html;
    }
  };

  // Update editor when code content changes in code mode (with auto-beautify)
  const handleCodeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newCode = e.target.value;
    setRawHtmlContent(newCode);
    // Auto-beautify the code for display
    const formatted = formatHTML(newCode);
    setCodeContent(formatted);
    onChange?.(newCode);
  };

  // Switch to WYSIWYG mode
  const handleSwitchToWysiwyg = () => {
    if (editor) {
      // Store the raw HTML (with style/script tags) before switching
      setRawHtmlContent(codeContent);
      // Update editor with the content
      editor.commands.setContent(codeContent);
      setEditorMode('wysiwyg');
    }
  };

  // Switch to Code mode
  const handleSwitchToCode = () => {
    if (editor) {
      // Use stored raw HTML if available (contains style/script tags), otherwise get from editor
      let htmlToFormat = rawHtmlContent;
      if (!htmlToFormat || htmlToFormat === '') {
        htmlToFormat = editor.getHTML();
      }
      // If switching from wysiwyg and we have raw content, use it
      if (editorMode === 'wysiwyg' && rawHtmlContent && rawHtmlContent !== editor.getHTML()) {
        htmlToFormat = rawHtmlContent;
      } else if (editorMode === 'wysiwyg') {
        htmlToFormat = editor.getHTML();
      }

      const formattedHtml = formatHTML(htmlToFormat);
      setCodeContent(formattedHtml);
      setRawHtmlContent(htmlToFormat);
      setEditorMode('code');
    }
  };

  // Format code in code mode
  const handleFormatCode = () => {
    const formatted = formatHTML(codeContent);
    setCodeContent(formatted);
    onChange?.(formatted);
  };

  return (
    <EditorContainer>
      {editable && (
        <>
          <ModeToggleContainer>
            <ModeToggleLabel>编辑模式:</ModeToggleLabel>
            <ModeToggle
              $active={editorMode === 'wysiwyg'}
              onClick={handleSwitchToWysiwyg}
              title="快捷键: Ctrl+Shift+H"
            >
              📝 可视化编辑
            </ModeToggle>
            <ModeToggle
              $active={editorMode === 'code'}
              onClick={handleSwitchToCode}
              title="快捷键: Ctrl+Shift+H"
            >
              💻 代码编辑
            </ModeToggle>
            {editorMode === 'code' && (
              <ModeToggle
                onClick={handleFormatCode}
                style={{ marginLeft: 'auto' }}
                title="格式化代码 (Ctrl+Shift+F)"
              >
                ✨ 格式化
              </ModeToggle>
            )}
          </ModeToggleContainer>
          {editorMode === 'wysiwyg' && <MenuBar editor={editor} />}
        </>
      )}
      <EditorContentWrapper>
        {editorMode === 'wysiwyg' ? (
          <EditorContent editor={editor} />
        ) : (
          <CodeEditorContainer>
            <textarea
              ref={codeEditorRef}
              value={codeContent}
              onChange={handleCodeChange}
              placeholder="在此输入HTML代码..."
              spellCheck={false}
            />
          </CodeEditorContainer>
        )}
      </EditorContentWrapper>
    </EditorContainer>
  );
};

export default TiptapEditor;
